import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Elements, PaymentElement, ExpressCheckoutElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import api, { formatApiError } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import AddressLookup from "@/components/checkout/AddressLookup";

const gbp = (n) => `£${Number(n).toLocaleString("en-GB", { minimumFractionDigits: 0 })}`;

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

function CheckoutInner({ orderId }) {
    const stripe = useStripe();
    const elements = useElements();
    const [submitting, setSubmitting] = useState(false);
    const [err, setErr] = useState("");
    const [elementReady, setElementReady] = useState(false);
    const [expressAvailable, setExpressAvailable] = useState(false);
    const { clear } = useCart();
    const navigate = useNavigate();

    const finishAndNavigate = () => {
        clear();
        navigate(`/order/${orderId}`);
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!stripe || !elements) {
            setErr("Payment is still loading. Please wait a moment and try again.");
            return;
        }
        setSubmitting(true);
        setErr("");
        // Safety timeout — if Stripe hangs (no response in 45s), let the buyer retry.
        const timeout = setTimeout(() => {
            setSubmitting(false);
            setErr("Payment is taking longer than expected. Please check your connection and try again — you will not be double-charged.");
        }, 45000);
        try {
            const returnUrl = `${window.location.origin}/order/${orderId}`;
            const { error } = await stripe.confirmPayment({
                elements,
                confirmParams: { return_url: returnUrl },
                redirect: "if_required",
            });
            clearTimeout(timeout);
            if (error) {
                setErr(error.message || "Payment failed. Please try again.");
                setSubmitting(false);
                return;
            }
            finishAndNavigate();
        } catch (e) {
            clearTimeout(timeout);
            setErr("Something went wrong contacting our payment provider. Please try again.");
            setSubmitting(false);
        }
    };

    // Apple Pay / Google Pay / Link — one-tap wallet buttons
    const onExpressConfirm = async () => {
        if (!stripe) return;
        setSubmitting(true);
        setErr("");
        const returnUrl = `${window.location.origin}/order/${orderId}`;
        try {
            const { error } = await stripe.confirmPayment({
                elements,
                confirmParams: { return_url: returnUrl },
                redirect: "if_required",
            });
            if (error) {
                setErr(error.message || "Payment failed. Please try again.");
                setSubmitting(false);
                return;
            }
            finishAndNavigate();
        } catch (e) {
            setErr("Wallet payment failed. Please try again or use a card below.");
            setSubmitting(false);
        }
    };

    return (
        <form onSubmit={onSubmit} data-testid="checkout-payment-form">
            {/* Apple Pay / Google Pay / Link — one-tap wallets */}
            <div data-testid="express-checkout-wrap" style={{ marginBottom: expressAvailable ? 20 : 0 }}>
                <ExpressCheckoutElement
                    onReady={({ availablePaymentMethods }) => {
                        // availablePaymentMethods is undefined when nothing is available on this device
                        setExpressAvailable(!!availablePaymentMethods && Object.values(availablePaymentMethods).some(Boolean));
                    }}
                    onConfirm={onExpressConfirm}
                    options={{
                        buttonType: { applePay: "buy", googlePay: "buy", paypal: "buynow" },
                        buttonHeight: 48,
                        // We already collected shipping in Step 1 — don't force the wallet to re-collect it
                        shippingAddressRequired: false,
                        emailRequired: false,
                    }}
                />
                {expressAvailable && (
                    <div style={{
                        display: "flex", alignItems: "center", gap: 12, margin: "16px 0 4px",
                        color: "var(--cocoa)", opacity: 0.5,
                    }}>
                        <div style={{ flex: 1, height: 1, background: "var(--sand-line)" }} />
                        <span style={{
                            fontFamily: "var(--font-sans)", fontSize: 10, letterSpacing: "0.24em",
                            textTransform: "uppercase",
                        }}>
                            or pay with card
                        </span>
                        <div style={{ flex: 1, height: 1, background: "var(--sand-line)" }} />
                    </div>
                )}
            </div>

            {!elementReady && (
                <div data-testid="stripe-loading" style={{
                    padding: 20, background: "var(--parchment-soft)", border: "1px solid var(--sand-line)",
                    fontFamily: "var(--font-serif)", fontStyle: "italic", color: "var(--cocoa)",
                    marginBottom: 16, textAlign: "center",
                }}>
                    Loading secure payment form…
                </div>
            )}
            <PaymentElement onReady={() => setElementReady(true)} />
            {err && <div className="form-error" style={{ marginTop: 12 }} data-testid="checkout-error">{err}</div>}
            <button
                type="submit"
                className="btn"
                disabled={!stripe || !elementReady || submitting}
                style={{ width: "100%", marginTop: 24 }}
                data-testid="checkout-pay-btn"
            >
                {submitting ? "Processing…" : elementReady ? "Pay Securely" : "Loading payment…"}
            </button>
        </form>
    );
}

export default function Checkout() {
    const { items, subtotal } = useCart();
    const { user } = useAuth();
    const [details, setDetails] = useState({
        email: user?.email || "",
        name: user?.name || "",
        line1: "",
        city: "",
        postal_code: "",
        country: "GB",
    });
    const [quote, setQuote] = useState(null);
    const [method, setMethod] = useState("standard");
    const [clientSecret, setClientSecret] = useState("");
    const [orderId, setOrderId] = useState("");
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");

    // Fetch shipping quote whenever country or subtotal changes
    useEffect(() => {
        if (!items.length) return;
        api.post("/shipping/quote", { country: details.country, subtotal })
            .then(({ data }) => {
                setQuote(data);
                if (!data.methods.find((m) => m.id === method)) {
                    setMethod(data.methods[0]?.id || "standard");
                }
            })
            .catch(() => setQuote(null));
    }, [details.country, subtotal, items.length]); // eslint-disable-line react-hooks/exhaustive-deps

    const chosenMethod = quote?.methods.find((m) => m.id === method) || quote?.methods[0];
    const shippingFee = chosenMethod?.fee ?? 0;
    const total = subtotal + shippingFee;

    const startCheckout = async (e) => {
        e.preventDefault();
        if (items.length === 0) return;
        setLoading(true);
        setErr("");
        try {
            const { data } = await api.post("/payments/create-intent", {
                product_ids: items.map((i) => i.id),
                email: details.email,
                name: details.name,
                shipping: {
                    line1: details.line1,
                    city: details.city,
                    postal_code: details.postal_code,
                    country: details.country,
                },
                shipping_method: method,
            });
            setClientSecret(data.client_secret);
            setOrderId(data.order_id);
        } catch (e) {
            setErr(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setLoading(false);
        }
    };

    if (items.length === 0 && !clientSecret) {
        return (
            <div className="checkout" style={{ gridTemplateColumns: "1fr" }} data-testid="checkout-empty">
                <div className="empty-state">
                    Your bag is empty.
                    <div style={{ marginTop: 16 }}>
                        <Link to="/shop" className="link-underline">Continue browsing</Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="checkout" data-testid="checkout-page">
            <div className="checkout-form">
                <div className="eyebrow" style={{ marginBottom: 8 }}>
                    {clientSecret ? "Checkout · Step 2 of 2" : "Checkout · Step 1 of 2"}
                </div>
                <h2>{clientSecret ? "Confirm your payment." : "Where should we send it?"}</h2>

                {!clientSecret ? (
                    <form onSubmit={startCheckout} data-testid="checkout-details-form">
                        <div className="checkout-fields">
                            <AddressLookup
                                country={details.country}
                                onFill={(patch) => setDetails((d) => ({ ...d, ...patch }))}
                                onManual={() => {
                                    // Focus the address field so buyer can type freely
                                    const el = document.getElementById("ck-line1");
                                    if (el) el.focus();
                                }}
                            />
                            <div className="field full">
                                <label className="label" htmlFor="ck-email">Email</label>
                                <input
                                    id="ck-email"
                                    className="input"
                                    type="email"
                                    required
                                    value={details.email}
                                    onChange={(e) => setDetails({ ...details, email: e.target.value })}
                                    data-testid="checkout-email"
                                    autoComplete="email"
                                />
                            </div>
                            <div className="field full">
                                <label className="label" htmlFor="ck-name">Full name</label>
                                <input
                                    id="ck-name"
                                    className="input"
                                    required
                                    value={details.name}
                                    onChange={(e) => setDetails({ ...details, name: e.target.value })}
                                    data-testid="checkout-name"
                                    autoComplete="name"
                                />
                            </div>
                            <div className="field full">
                                <label className="label" htmlFor="ck-line1">Address</label>
                                <input
                                    id="ck-line1"
                                    className="input"
                                    required
                                    value={details.line1}
                                    onChange={(e) => setDetails({ ...details, line1: e.target.value })}
                                    data-testid="checkout-line1"
                                    autoComplete="address-line1"
                                    placeholder="Flat / house number and street"
                                />
                            </div>
                            <div className="field">
                                <label className="label" htmlFor="ck-city">City</label>
                                <input
                                    id="ck-city"
                                    className="input"
                                    required
                                    value={details.city}
                                    onChange={(e) => setDetails({ ...details, city: e.target.value })}
                                    data-testid="checkout-city"
                                    autoComplete="address-level2"
                                />
                            </div>
                            <div className="field">
                                <label className="label" htmlFor="ck-post">Postcode</label>
                                <input
                                    id="ck-post"
                                    className="input"
                                    required
                                    value={details.postal_code}
                                    onChange={(e) => setDetails({ ...details, postal_code: e.target.value })}
                                    data-testid="checkout-postcode"
                                    autoComplete="postal-code"
                                />
                            </div>
                            <div className="field full">
                                <label className="label" htmlFor="ck-country">Country</label>
                                <select
                                    id="ck-country"
                                    className="input"
                                    value={details.country}
                                    onChange={(e) => setDetails({ ...details, country: e.target.value })}
                                    data-testid="checkout-country"
                                >
                                    <option value="GB">United Kingdom</option>
                                    <option value="IE">Ireland</option>
                                    <option value="FR">France</option>
                                    <option value="DE">Germany</option>
                                    <option value="NL">Netherlands</option>
                                    <option value="BE">Belgium</option>
                                    <option value="LU">Luxembourg</option>
                                    <option value="IT">Italy</option>
                                    <option value="ES">Spain</option>
                                    <option value="PT">Portugal</option>
                                    <option value="AT">Austria</option>
                                    <option value="SE">Sweden</option>
                                    <option value="DK">Denmark</option>
                                    <option value="FI">Finland</option>
                                    <option value="GR">Greece</option>
                                    <option value="US">United States</option>
                                    <option value="CA">Canada</option>
                                    <option value="AU">Australia</option>
                                    <option value="NZ">New Zealand</option>
                                    <option value="JP">Japan</option>
                                    <option value="AE">United Arab Emirates</option>
                                    <option value="SG">Singapore</option>
                                    <option value="HK">Hong Kong</option>
                                    <option value="CH">Switzerland</option>
                                    <option value="NO">Norway</option>
                                </select>
                                {quote?.duty_note && (
                                    <div style={{ marginTop: 8, fontFamily: "var(--font-serif)", fontStyle: "italic", fontSize: 13, color: "var(--cocoa-soft)" }}>
                                        {quote.duty_note}
                                    </div>
                                )}
                            </div>

                            {quote?.methods && quote.methods.length > 0 && (
                                <div className="field full">
                                    <label className="label">Shipping method — {quote.zone.name}</label>
                                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                                        {quote.methods.map((m) => (
                                            <label
                                                key={m.id}
                                                style={{
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    alignItems: "center",
                                                    padding: "14px 16px",
                                                    border: `1px solid ${method === m.id ? "var(--cocoa)" : "var(--sand)"}`,
                                                    background: method === m.id ? "var(--parchment-soft)" : "transparent",
                                                    cursor: "pointer",
                                                    fontFamily: "var(--font-serif)",
                                                }}
                                                data-testid={`shipping-method-${m.id}`}
                                            >
                                                <span style={{ display: "flex", alignItems: "center", gap: 12 }}>
                                                    <input
                                                        type="radio"
                                                        name="shipping-method"
                                                        value={m.id}
                                                        checked={method === m.id}
                                                        onChange={() => setMethod(m.id)}
                                                        style={{ accentColor: "var(--cocoa)" }}
                                                    />
                                                    {m.name}
                                                </span>
                                                <strong>{m.is_free ? "Free" : gbp(m.fee)}</strong>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                        {err && <div className="form-error">{err}</div>}
                        <button
                            className="btn"
                            type="submit"
                            disabled={loading}
                            style={{ width: "100%" }}
                            data-testid="checkout-continue-btn"
                        >
                            {loading ? "Preparing…" : "Continue to Payment"}
                        </button>
                    </form>
                ) : (
                    <>
                        <button
                            type="button"
                            onClick={() => { setClientSecret(""); setOrderId(""); }}
                            data-testid="checkout-edit-details"
                            style={{
                                background: "transparent", border: "none", padding: 0, marginBottom: 16,
                                fontFamily: "var(--font-sans)", fontSize: 10, letterSpacing: "0.24em",
                                textTransform: "uppercase", color: "var(--cocoa)", cursor: "pointer",
                                textDecoration: "underline",
                            }}
                        >
                            ← Edit delivery details
                        </button>
                        <Elements
                        stripe={stripePromise}
                        options={{
                            clientSecret,
                            appearance: {
                                theme: "flat",
                                variables: {
                                    colorPrimary: "#4E3629",
                                    colorBackground: "#FAF6F0",
                                    colorText: "#4E3629",
                                    colorDanger: "#7a2f22",
                                    fontFamily: "'Cormorant Garamond', Georgia, serif",
                                    borderRadius: "0px",
                                    spacingUnit: "5px",
                                },
                                rules: {
                                    ".Input": { border: "1px solid #C4A882", padding: "14px 16px", fontSize: "16px" },
                                    ".Label": { fontFamily: "Inter, sans-serif", fontSize: "10px", letterSpacing: "0.28em", textTransform: "uppercase", color: "#C4A882" },
                                },
                            },
                        }}
                    >
                        <CheckoutInner orderId={orderId} />
                    </Elements>
                    </>
                )}
            </div>

            <div className="checkout-summary" data-testid="checkout-summary">
                <h2>Your order</h2>
                {items.map((it) => (
                    <div key={it.id} className="line">
                        <div>
                            <div style={{ fontFamily: "var(--font-sans)", fontSize: 10, letterSpacing: "0.24em", textTransform: "uppercase", color: "var(--sand)" }}>
                                {it.designer}
                            </div>
                            <div>{it.title}</div>
                            <div style={{ fontSize: 12, color: "var(--cocoa-soft)" }}>Size {it.size}</div>
                        </div>
                        <strong>{gbp(it.price)}</strong>
                    </div>
                ))}
                <div className="line" style={{ marginTop: 8 }}>
                    <span>Subtotal</span>
                    <strong>{gbp(subtotal)}</strong>
                </div>
                <div className="line">
                    <span>Shipping{chosenMethod ? ` — ${chosenMethod.name}` : ""}</span>
                    <strong>{chosenMethod?.is_free ? "Free" : gbp(shippingFee)}</strong>
                </div>
                <div className="total">
                    <span>Total</span>
                    <strong>{gbp(total)}</strong>
                </div>
            </div>
        </div>
    );
}
